import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { AppleButton } from "@/components/ui/apple-button";
import { LottiePlayer } from "@/components/ui/lottie-player";
import PageTransition from "@/components/ui/PageTransition";
import LanguageSwitcher from "@/components/ui/LanguageSwitcher";
import { supabase } from "@/integrations/supabase/client";
import { ArrowRight, ShoppingCart, Sticker, QrCode, Scan } from "lucide-react";
import { useKeenSlider } from 'keen-slider/react';
import "keen-slider/keen-slider.min.css";

const Index = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>({
    initial: 0,
    slideChanged(slider) {
      setCurrentSlide(slider.track.details.rel);
    },
    slides: {
      perView: 1,
      spacing: 16,
    },
  });

  useEffect(() => {
    const checkAuth = async () => {
      // Don't redirect if we're on the public scan page
      if (location.pathname === "/public/scan") return;

      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        navigate("/overview");
      }
    };
    checkAuth();
  }, [navigate, location.pathname]);

  return (
    <PageTransition>
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-gray-50 to-white text-center px-4">
        <LanguageSwitcher />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-7xl mx-auto space-y-8"
        >
          {/* Animation */}
          <div className="w-[130px] h-[130px] mx-auto mt-12 mb-4">
            <LottiePlayer
              src="https://lottie.host/a5bba61b-ca65-466e-885a-467856ae4456/VPzIzTB6RK.lottie"
              style={{ width: '100%', height: '100%' }}
            />
          </div>

          {/* Badge */}
          <div className="inline-flex px-4 py-2 rounded-full text-sm font-medium bg-blue-50 text-blue-600">
            {t('welcome')}
          </div>
          
          {/* Main Heading */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-semibold text-gray-800 leading-tight mb-6 max-w-5xl mx-auto">
            {t('tagline.part1')} <br className="hidden sm:block" />
            {t('tagline.part2')}
          </h1>
          
          {/* Description */}
          <p className="text-xl text-gray-600 mt-6 mb-8 max-w-4xl mx-auto">
            {t('description')}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col items-center gap-4">
            <AppleButton onClick={() => navigate("/public/scan")} variant="secondary">
              {t('buttons.publicScan')}
              <ArrowRight className="ml-2 h-4 w-4" />
            </AppleButton>
            <AppleButton
              onClick={() => navigate("/auth")}
              className="text-lg px-8 py-6 flex items-center space-x-2"
            >
              <span>{t('buttons.getStarted')}</span>
              <ArrowRight className="w-5 h-5 ml-2" />
            </AppleButton>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 max-w-7xl mx-auto">
            <div className="p-6 rounded-2xl bg-white shadow-sm border border-gray-100">
              <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-4 mx-auto">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2">{t('features.secureAccess.title')}</h3>
              <p className="text-gray-600">{t('features.secureAccess.description')}</p>
            </div>
            <div className="p-6 rounded-2xl bg-white shadow-sm border border-gray-100">
              <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-4 mx-auto">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2">{t('features.teamCollaboration.title')}</h3>
              <p className="text-gray-600">{t('features.teamCollaboration.description')}</p>
            </div>
            <div className="p-6 rounded-2xl bg-white shadow-sm border border-gray-100">
              <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-4 mx-auto">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2">{t('features.easyTracking.title')}</h3>
              <p className="text-gray-600">{t('features.easyTracking.description')}</p>
            </div>
          </div>

          {/* Mobile Experience */}
          <div className="mt-24">
            <h3 className="text-xl font-semibold text-gray-800 mb-8 text-center">
              {t('system.mobile')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8 max-w-[900px] mx-auto px-4 mb-24">
              <div className="rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow max-w-[250px] mx-auto w-full">
                <img
                  src="/images/tivoscan.jpg"
                  alt="Mobile Scan View"
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
              </div>
              <div className="rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow max-w-[250px] mx-auto w-full">
                <img
                  src="/images/tivoassets.jpg"
                  alt="Mobile Assets View"
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
              </div>
              <div className="rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow max-w-[250px] mx-auto w-full">
                <img
                  src="/images/tivoassetsdesc.jpg"
                  alt="Mobile Asset Description"
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
              </div>
            </div>

            {/* Dashboard Experience */}
            <div className="mt-24">
              <h3 className="text-xl font-semibold text-gray-800 mb-8 text-center">
                {t('system.dashboard')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-7xl mx-auto px-4">
                <div className="rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                  <img
                    src="/images/tivodashAsset2.jpg"
                    alt="Dashboard Assets View"
                    className="w-full h-auto object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                  <img
                    src="/images/tivodashRegistros.jpg"
                    alt="Dashboard Scan View"
                    className="w-full h-auto object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>

            {/* Points of Information Section */}
            <div className="mt-40 relative overflow-hidden py-20">
              {/* Remove gradient background */}
              <div className="max-w-7xl mx-auto px-4">
                <div className="text-center mb-16">
                  <h2 className="inline-flex px-8 py-4 rounded-full text-2xl font-semibold bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors mb-8">
                    {t('points.title') || 'Points of Information'}
                  </h2>
                  <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                    {t('points.headline') || 'Transform Any Object into an Interactive Knowledge Hub'}
                  </h3>
                  <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                    {t('points.description') || 'Beyond asset tracking, Tivo creates a network of smart information points. Every tag becomes a gateway to knowledge, instructions, and real-time data.'}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
                  {/* Digital Instructions */}
                  <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all border-2 border-blue-50">
                    <div className="w-16 h-16 bg-blue-50 rounded-xl flex items-center justify-center mb-6">
                      <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <h4 className="text-xl font-semibold mb-4">{t('points.features.instructions.title') || 'Digital Instructions'}</h4>
                    <p className="text-gray-600">{t('points.features.instructions.description') || 'Attach manuals, guides, and step-by-step instructions to any object. Access them instantly with a simple scan.'}</p>
                  </div>

                  {/* Live Documentation */}
                  <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all border-2 border-blue-50">
                    <div className="w-16 h-16 bg-blue-50 rounded-xl flex items-center justify-center mb-6">
                      <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                      </svg>
                    </div>
                    <h4 className="text-xl font-semibold mb-4">{t('points.features.documentation.title') || 'Live Documentation'}</h4>
                    <p className="text-gray-600">{t('points.features.documentation.description') || 'Create living documents that evolve with your assets. Keep records, maintenance logs, and important notes always accessible.'}</p>
                  </div>

                  {/* Smart Information */}
                  <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all border-2 border-blue-50">
                    <div className="w-16 h-16 bg-blue-50 rounded-xl flex items-center justify-center mb-6">
                      <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    <h4 className="text-xl font-semibold mb-4">{t('points.features.smart.title') || 'Smart Information'}</h4>
                    <p className="text-gray-600">{t('points.features.smart.description') || 'Transform static objects into smart information points. Connect real-world items to your digital knowledge base.'}</p>
                  </div>
                </div>

                {/* Interactive Showcase */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mt-20">
                  <div className="space-y-8">
                    <h3 className="text-3xl font-bold text-gray-900">
                      {t('points.showcase.title') || 'The Future of Information Access'}
                    </h3>
                    <div className="space-y-6">
                      {/* Feature 1 */}
                      <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0 w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                          <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                          </svg>
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold mb-2">{t('points.showcase.feature1.title') || 'Contextual Information'}</h4>
                          <p className="text-gray-600">{t('points.showcase.feature1.description') || 'Information adapts to the user, location, and time. Get relevant details exactly when you need them.'}</p>
                        </div>
                      </div>

                      {/* Feature 2 */}
                      <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0 w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                          <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                          </svg>
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold mb-2">{t('points.showcase.feature2.title') || 'Interactive Experience'}</h4>
                          <p className="text-gray-600">{t('points.showcase.feature2.description') || 'Create rich, interactive experiences. Embed videos, 3D models, and augmented reality content.'}</p>
                        </div>
                      </div>

                      {/* Feature 3 */}
                      <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0 w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                          <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                          </svg>
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold mb-2">{t('points.showcase.feature3.title') || 'Secure Access Control'}</h4>
                          <p className="text-gray-600">{t('points.showcase.feature3.description') || 'Control who sees what. Set permissions and access levels for different users and roles.'}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Futuristic Visual */}
                  <div className="relative">
                    <div className="aspect-[3/4] sm:aspect-square relative rounded-[2rem] overflow-hidden bg-gradient-to-br from-white via-blue-50 to-blue-100 shadow-2xl border-[1px] border-blue-100">
                      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-blue-100/40 via-transparent to-transparent" />
                      <div className="absolute inset-0 flex items-center justify-center flex-col p-4 sm:p-8">
                        <div className="w-full max-w-[300px] aspect-square mx-auto">
                          <LottiePlayer
                            src="https://lottie.host/4549c427-d46b-4fd9-85d0-30b028abc26f/PE0X1jCEEE.lottie"
                            style={{ width: '100%', height: '100%' }}
                          />
                        </div>
                        <h4 className="text-xl sm:text-2xl font-bold mb-2 sm:mb-4 text-gray-900 mt-4">{t('points.showcase.visual.title') || 'Points of Information Everywhere'}</h4>
                        <p className="text-base sm:text-lg text-gray-600 px-2">{t('points.showcase.visual.description') || 'Turn any object into a digital information point with Tivo tags'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tivo NFC Tag Product Showcase */}
            <div className="mt-40 relative overflow-hidden py-20">
              {/* Background gradient effect */}
              <div className="absolute inset-0 bg-gradient-to-b from-white to-blue-50/30 pointer-events-none" />
              
              <div className="max-w-7xl mx-auto px-4 relative z-10">
                {/* Product Title */}
                <div className="text-center mb-20">
                  <h2 className="text-6xl md:text-7xl font-bold text-gray-900 tracking-tight mb-6">
                    {t('nfcTag.title')}
                  </h2>
                  <p className="text-2xl md:text-3xl text-gray-800 mb-8">
                    {t('nfcTag.subtitle')}
                  </p>
                  <a
                    href="https://buy.stripe.com/fZe4gV9IP4C89z228h"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-8 py-4 rounded-full text-lg font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-colors shadow-lg hover:shadow-xl"
                  >
                    {t('nfcTag.buyButton')}
                    <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </a>
                </div>

                {/* Product Display */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                  {/* Image Side */}
                  <div className="relative">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: 0.2 }}
                      className="relative"
                    >
                      <div className="aspect-square relative rounded-[2rem] overflow-hidden bg-white shadow-2xl border-8 border-white">
                        <img
                          src="/images/tivoTaggStore.jpg"
                          alt={t('nfcTag.title')}
                          className="absolute inset-0 w-full h-full object-cover transform hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      {/* Floating specs */}
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.5, delay: 0.8 }}
                        className="absolute -right-2 top-8 bg-white rounded-xl p-4 shadow-xl border-2 border-white"
                      >
                        <p className="text-base font-bold text-gray-900">{t('nfcTag.specs.frequency.value')}</p>
                        <p className="text-sm text-gray-800">{t('nfcTag.specs.frequency.label')}</p>
                      </motion.div>
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.5, delay: 1 }}
                        className="absolute -left-2 bottom-8 bg-white rounded-xl p-4 shadow-xl border-2 border-white"
                      >
                        <p className="text-base font-bold text-gray-900">{t('nfcTag.specs.guarantee.value')}</p>
                        <p className="text-sm text-gray-800">{t('nfcTag.specs.guarantee.label')}</p>
                      </motion.div>
                    </motion.div>
                  </div>

                  {/* Specs Side */}
                  <div className="space-y-8">
                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.8 }}
                      className="space-y-8"
                    >
                      {/* Key Features Grid */}
                      <div className="grid grid-cols-2 gap-8">
                        <div className="bg-white rounded-2xl p-8 shadow-2xl border-4 border-white hover:bg-blue-50/10 transition-colors">
                          <h3 className="text-xl font-bold text-gray-900 mb-3">{t('nfcTag.features.weatherResistant.title')}</h3>
                          <p className="text-lg text-gray-800">{t('nfcTag.features.weatherResistant.value')}</p>
                        </div>
                        <div className="bg-white rounded-2xl p-8 shadow-2xl border-4 border-white hover:bg-blue-50/10 transition-colors">
                          <h3 className="text-xl font-bold text-gray-900 mb-3">{t('nfcTag.features.durability.title')}</h3>
                          <p className="text-lg text-gray-800">{t('nfcTag.features.durability.value')}</p>
                        </div>
                        <div className="bg-white rounded-2xl p-8 shadow-2xl border-4 border-white hover:bg-blue-50/10 transition-colors">
                          <h3 className="text-xl font-bold text-gray-900 mb-3">{t('nfcTag.features.metalCompatible.title')}</h3>
                          <p className="text-lg text-gray-800">{t('nfcTag.features.metalCompatible.value')}</p>
                        </div>
                        <div className="bg-white rounded-2xl p-8 shadow-2xl border-4 border-white hover:bg-blue-50/10 transition-colors">
                          <h3 className="text-xl font-bold text-gray-900 mb-3">{t('nfcTag.features.easyApplication.title')}</h3>
                          <p className="text-lg text-gray-800">{t('nfcTag.features.easyApplication.value')}</p>
                        </div>
                      </div>

                      {/* Protocol Info */}
                      <div className="bg-white rounded-2xl p-8 shadow-2xl border-4 border-white">
                        <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('nfcTag.protocol.title')}</h3>
                        <p className="text-lg text-gray-800 mb-6">
                          {t('nfcTag.protocol.description')}
                        </p>
                        <div className="flex items-center gap-6">
                          <div className="h-16 w-16 rounded-2xl bg-blue-50 flex items-center justify-center border-2 border-blue-100">
                            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
                            </svg>
                          </div>
                          <div className="h-16 w-16 rounded-2xl bg-blue-50 flex items-center justify-center border-2 border-blue-100">
                            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                </div>

                {/* Mobile Compatibility Banner */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 1.2 }}
                  className="mt-24 text-center bg-white rounded-3xl p-12 shadow-2xl border-4 border-white"
                >
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">
                    {t('nfcTag.compatibility.title')}
                  </h3>
                  <p className="text-xl text-gray-800 max-w-2xl mx-auto">
                    {t('nfcTag.compatibility.description')}
                  </p>
                </motion.div>
              </div>
            </div>

            {/* Story Section */}
            <div className="mt-40 text-center max-w-6xl mx-auto">
              <h2 className="inline-flex px-8 py-4 mb-8 rounded-full text-2xl font-semibold bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors">
                {t('story.title')}
              </h2>
              <p className="text-xl text-gray-700 mb-12 leading-relaxed">
                {t('story.description')}
              </p>
            </div>

            {/* Testimonials Section - Moved here */}
            <div className="relative mt-40">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-blue-600">
                  {t('testimonials.title')}
                </h2>
              </div>
              
              <div className="max-w-3xl mx-auto px-4">
                <div className="space-y-8">
                  {(t('testimonials.items', { returnObjects: true }) as any[]).map((testimonial, index) => (
                    <div key={index} className="bg-white rounded-3xl p-8 sm:p-12 shadow-lg hover:shadow-xl transition-all">
                      <div className="flex flex-col items-center text-center">
                        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-blue-50 rounded-full flex items-center justify-center mb-6 sm:mb-8">
                          <svg className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                          </svg>
                        </div>
                        <blockquote className="text-lg sm:text-xl text-gray-700 mb-6 leading-relaxed max-w-2xl">
                          "{testimonial.text}"
                        </blockquote>
                        <div className="mt-2">
                          <cite className="text-lg sm:text-xl font-semibold text-gray-900 not-italic block mb-1">
                            {testimonial.author}
                          </cite>
                          <p className="text-gray-500">
                            {testimonial.role}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {/* Steps Section */}
          <div className="mt-24 mb-24 max-w-7xl mx-auto">
            <div className="flex justify-center mb-12">
              <h2 className="inline-flex px-8 py-4 rounded-full text-2xl font-semibold bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors">
                {t('steps.title')}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white rounded-2xl p-8 shadow-lg relative overflow-hidden group hover:shadow-xl transition-all"
              >
                <div className="absolute -top-4 -left-4 w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  1
                </div>
                <div className="mb-6 transform group-hover:scale-110 transition-transform">
                  <ShoppingCart className="h-10 w-10 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold mb-4">{t('steps.buyTags.title')}</h3>
                <p className="text-gray-600 mb-4">{t('steps.buyTags.description')}</p>
                <a
                  href="https://buy.stripe.com/fZe4gV9IP4C89z228h"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                >
                  {t('nfcTag.buyButton')}
                  <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </a>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-white rounded-2xl p-8 shadow-lg relative overflow-hidden group hover:shadow-xl transition-all"
              >
                <div className="absolute -top-4 -left-4 w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  2
                </div>
                <div className="mb-6 transform group-hover:scale-110 transition-transform">
                  <Sticker className="h-10 w-10 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold mb-4">{t('steps.applyTag.title')}</h3>
                <p className="text-gray-600">{t('steps.applyTag.description')}</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="bg-white rounded-2xl p-8 shadow-lg relative overflow-hidden group hover:shadow-xl transition-all"
              >
                <div className="absolute -top-4 -left-4 w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  3
                </div>
                <div className="mb-6 transform group-hover:scale-110 transition-transform">
                  <QrCode className="h-10 w-10 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold mb-4">{t('steps.scanTrack.title')}</h3>
                <p className="text-gray-600">{t('steps.scanTrack.description')}</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="bg-white rounded-2xl p-8 shadow-lg relative overflow-hidden group hover:shadow-xl transition-all border-2 border-blue-100"
              >
                <div className="absolute -top-4 -left-4 w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  +
                </div>
                <div className="mb-6 transform group-hover:scale-110 transition-transform">
                  <Scan className="h-10 w-10 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold mb-4">{t('steps.existingTags.title')}</h3>
                <p className="text-gray-600">{t('steps.existingTags.description')}</p>
              </motion.div>
            </div>
          </div>

            {/* Use Cases Grid */}
            <div className="mt-40 mb-20 max-w-7xl mx-auto">
              <div className="flex justify-center mb-12">
                <h2 className="inline-flex px-8 py-4 rounded-full text-2xl font-semibold bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors">
                  {t('industries.title')}
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {/* Home Management */}
                <div className="p-6 rounded-2xl bg-gradient-to-br from-blue-50 to-white shadow-sm border border-gray-100">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4 mx-auto">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold mb-3">{t('industries.smartHome.title')}</h3>
                  <ul className="text-gray-600 space-y-2">
                    {(t('industries.smartHome.items', { returnObjects: true }) as string[]).map((item, index) => (
                      <li key={index}>• {item}</li>
                    ))}
                  </ul>
                </div>

                {/* Real Estate */}
                <div className="p-6 rounded-2xl bg-gradient-to-br from-blue-50 to-white shadow-sm border border-gray-100">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4 mx-auto">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold mb-3">{t('industries.realEstate.title')}</h3>
                  <ul className="text-gray-600 space-y-2">
                    {(t('industries.realEstate.items', { returnObjects: true }) as string[]).map((item, index) => (
                      <li key={index}>• {item}</li>
                    ))}
                  </ul>
                </div>

                {/* Fleet Management */}
                <div className="p-6 rounded-2xl bg-gradient-to-br from-blue-50 to-white shadow-sm border border-gray-100">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4 mx-auto">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold mb-3">{t('industries.fleet.title')}</h3>
                  <ul className="text-gray-600 space-y-2">
                    {(t('industries.fleet.items', { returnObjects: true }) as string[]).map((item, index) => (
                      <li key={index}>• {item}</li>
                    ))}
                  </ul>
                </div>

                {/* Manufacturing */}
                <div className="p-6 rounded-2xl bg-gradient-to-br from-blue-50 to-white shadow-sm border border-gray-100">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4 mx-auto">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold mb-3">{t('industries.manufacturing.title')}</h3>
                  <ul className="text-gray-600 space-y-2">
                    {(t('industries.manufacturing.items', { returnObjects: true }) as string[]).map((item, index) => (
                      <li key={index}>• {item}</li>
                    ))}
                  </ul>
                </div>

                {/* Healthcare */}
                <div className="p-6 rounded-2xl bg-gradient-to-br from-blue-50 to-white shadow-sm border border-gray-100">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4 mx-auto">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold mb-3">{t('industries.healthcare.title')}</h3>
                  <ul className="text-gray-600 space-y-2">
                    {(t('industries.healthcare.items', { returnObjects: true }) as string[]).map((item, index) => (
                      <li key={index}>• {item}</li>
                    ))}
                  </ul>
                </div>

                {/* IT Asset Management */}
                <div className="p-6 rounded-2xl bg-gradient-to-br from-blue-50 to-white shadow-sm border border-gray-100">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4 mx-auto">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold mb-3">{t('industries.it.title')}</h3>
                  <ul className="text-gray-600 space-y-2">
                    {(t('industries.it.items', { returnObjects: true }) as string[]).map((item, index) => (
                      <li key={index}>• {item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* How to Use Tivo Section */}
          <div className="mt-40 max-w-7xl mx-auto">
            <div className="flex justify-center mb-12">
              <h2 className="inline-flex px-8 py-4 rounded-full text-2xl font-semibold bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors">
                {t('howToUse.title')}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((step) => (
                <div key={step} className="p-6 rounded-2xl bg-white shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                  <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-4 mx-auto">
                    {step === 1 && (
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                    )}
                    {step === 2 && (
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    )}
                    {step === 3 && (
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                      </svg>
                    )}
                    {step === 4 && (
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    )}
                    {step === 5 && (
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    )}
                    {step === 6 && (
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )}
                  </div>
                  <h3 className="text-lg font-semibold mb-2">
                    {t(`howToUse.steps.${step}.title`)}
                  </h3>
                  {step === 1 ? (
                    <>
                      <p className="text-gray-600 mb-4">
                        {t(`howToUse.steps.${step}.description`)}
                      </p>
                      <a
                        href="https://buy.stripe.com/fZe4gV9IP4C89z228h"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                      >
                        {t('nfcTag.buyButton')}
                        <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                      </a>
                    </>
                  ) : (
                    <p className="text-gray-600">
                      {t(`howToUse.steps.${step}.description`)}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* System Screenshots Section */}
          <div className="mt-40 max-w-7xl mx-auto">
            <div className="flex justify-center mb-12">
              <h2 className="inline-flex px-8 py-4 rounded-full text-2xl font-semibold bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors">
                {t('system.title')}
              </h2>
            </div>

            {/* Dashboard Views */}
            <div className="mb-20">
              <h3 className="text-xl font-semibold text-gray-800 mb-8 text-center">
                {t('system.dashboard')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-7xl mx-auto">
                <div className="rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                  <img
                    src="/images/tivo_dash_assets.jpg"
                    alt="Dashboard Assets View"
                    className="w-full h-auto object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                  <img
                    src="/images/tivo_dash_scan.jpg"
                    alt="Dashboard Scan View"
                    className="w-full h-auto object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Final Wrap-up Section */}
          <div className="mt-40 mb-20 relative overflow-hidden">
            <div className="max-w-7xl mx-auto px-4">
              <div className="text-center mb-16">
                <h2 className="inline-flex px-8 py-4 rounded-full text-2xl font-semibold bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors mb-8">
                  {t('wrapUp.title')}
                </h2>
                <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                  {t('wrapUp.headline')}
                </h3>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  {t('wrapUp.description')}
                </p>
              </div>

              {/* Benefits Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
                {/* Time Savings */}
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all border border-blue-100">
                  <div className="w-16 h-16 bg-blue-50 rounded-xl flex items-center justify-center mb-6">
                    <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h4 className="text-xl font-semibold mb-4">{t('wrapUp.benefits.time.title')}</h4>
                  <p className="text-gray-600">{t('wrapUp.benefits.time.description')}</p>
                </div>

                {/* Money Savings */}
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all border border-blue-100">
                  <div className="w-16 h-16 bg-blue-50 rounded-xl flex items-center justify-center mb-6">
                    <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h4 className="text-xl font-semibold mb-4">{t('wrapUp.benefits.money.title')}</h4>
                  <p className="text-gray-600">{t('wrapUp.benefits.money.description')}</p>
                </div>

                {/* Reminders */}
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all border border-blue-100">
                  <div className="w-16 h-16 bg-blue-50 rounded-xl flex items-center justify-center mb-6">
                    <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                  </div>
                  <h4 className="text-xl font-semibold mb-4">{t('wrapUp.benefits.reminders.title')}</h4>
                  <p className="text-gray-600">{t('wrapUp.benefits.reminders.description')}</p>
                </div>

                {/* Peace of Mind */}
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all border border-blue-100">
                  <div className="w-16 h-16 bg-blue-50 rounded-xl flex items-center justify-center mb-6">
                    <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <h4 className="text-xl font-semibold mb-4">{t('wrapUp.benefits.peace.title')}</h4>
                  <p className="text-gray-600">{t('wrapUp.benefits.peace.description')}</p>
                </div>
              </div>

              {/* For Everyone Section */}
              <div className="text-center mb-16">
                <h3 className="text-2xl font-bold text-gray-900 mb-8">
                  {t('wrapUp.forEveryone.title')}
                </h3>
                <div className="flex flex-wrap justify-center gap-4">
                  {['individuals', 'families', 'businesses', 'organizations'].map((audience) => (
                    <div key={audience} className="px-6 py-3 bg-blue-50 rounded-full text-blue-600 font-medium">
                      {t(`wrapUp.forEveryone.${audience}`)}
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA Button */}
              <div className="text-center">
                <button
                  onClick={() => navigate("/auth")}
                  className="inline-flex items-center px-8 py-4 rounded-full text-lg font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-colors shadow-lg hover:shadow-xl"
                >
                  {t('wrapUp.cta')}
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Footer */}
          <footer className="mt-20 pb-8 text-center">
            <div className="max-w-7xl mx-auto px-4">
              <div className="pt-8 border-t border-gray-200">
                <nav className="flex flex-wrap justify-center gap-x-8 gap-y-4 mb-4">
                  <a
                    href="/privacy-policy"
                    className="text-gray-500 hover:text-gray-900 transition-colors"
                  >
                    {t('footer.privacyPolicy')}
                  </a>
                  <a
                    href="mailto:ai@whisperlabs.ai"
                    className="text-gray-500 hover:text-gray-900 transition-colors"
                  >
                    {t('footer.contactUs')}
                  </a>
                </nav>
                <p className="text-gray-400 text-sm">
                  © {new Date().getFullYear()} Tivo Asset Manager. {t('footer.rights')}
                  <a 
                    href="https://www.whisperlabs.ai" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="hover:text-gray-600 transition-colors"
                  >
                    Whisper Labs
                  </a>
                </p>
              </div>
            </div>
          </footer>
        </motion.div>
      </div>
    </PageTransition>
  );
};

export default Index;